import './apps.scss';
import React, {useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useStores} from 'src/components/store';
import {AppsFilterNames, Direction, MainPages, ProtectionMenuNameOptions} from 'src/utils/enums';
import Loader from '../../shared/loader/loader';
import {Filters} from '../../shared/filters/filters';
import {useAppsFiltersConfig} from '../../shared/filters/filter-config/apps-filters';
import {NoResult} from '../../shared/no-result/no-result';
import {HeaderColumnMax} from '../protection/header-column-max/header-column-max';
import {AppWithStatus} from '@src/dtos/app.dto';
import CustomIcon, {IconType} from '../../shared/custom-icon/custom-icon';
import CustomButton from '../../shared/custom-button/custom-button';
import {ManagedAppsSelectModal} from './managed-apps-select/managed-apps-select';
import {DefaultBehavior} from './default-behavior';
import usePromiseFeedback from 'src/components/shared/promise-feedback/usePromiseFeedback';
import {toastSuccessUpdateManageApp} from './apps-utils';
import _, { kebabCase } from 'lodash';
import {DependenciesWarningModal, DependenciesWarningModalProps} from './warnings/dependencies-warning-modal';
import {FwVersionContent} from 'src/dtos/version.dto';
import {TooltipDisableReason} from '../../shared/tooltip-disable-message/tooltip-disable-reason';
import {getCurrentFilterLabel} from '@src/utils/filters-util';
import {AppItem} from './app-item';
import {ListItem} from '../../shared/entity-selector/entity-selector';
import {arrayToMap} from '@src/utils/map-and-array';
import {usePageViewEvent} from '../../shared/hooks/use-page-view-event';
import { AiAppsUsageBanner } from '@src/components/features/apps-services/ai-apps-usage-banner';

export const AppsPolicy: React.FC = observer(() => {
    const {fwVersionStore, ruleStore} = useStores();
    const {managedApps} = fwVersionStore;
    const [apps, setApps] = useState<AppWithStatus[]|undefined>(undefined);
    const [appsVisible, setAppsVisible] = useState(false);
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [dependenciesWarningModalData, setDependenciesWarningModalData] = useState<DependenciesWarningModalProps | null>(null);

    const appsFiltersConfig = useAppsFiltersConfig();

    usePageViewEvent(MainPages.POLICY, ProtectionMenuNameOptions.APPS);

    useEffect(() => {
        fwVersionStore.setProtectionType(ProtectionMenuNameOptions.APPS);
    }, []);

    const filterLabel = getCurrentFilterLabel(appsFiltersConfig.localStorageKey || '', appsFiltersConfig.radioButtons?.options || []);
    const isAiDefenseFilter = filterLabel === AppsFilterNames.AiDefense;

    useEffect(() => {
        if (isAiDefenseFilter) {
            fwVersionStore.fetchAiApps();
        }
    }, [isAiDefenseFilter]);

    const originalApps: AppWithStatus[] = useMemo(() => {
        return isAiDefenseFilter ? fwVersionStore.aiDefenseApps : managedApps;
    }, [managedApps, isAiDefenseFilter, fwVersionStore.aiDefenseApps]);

    const managedAppIds = useMemo(() => arrayToMap(fwVersionStore.managedApps, _ => _.id, _ => _.id),
        [fwVersionStore.managedApps]);

    const updateApp = async (id: string, direction: Direction, isAllow: boolean) => {
        setDependenciesWarningModalData(null);
        await fwVersionStore.setIsDirty();
        const originalData = _.cloneDeep(apps) || [];
        const appsCopy = _.cloneDeep(apps) || [];
        const copiedApp = appsCopy.find(_ => _.id === id);
        if (!copiedApp) return;

        copiedApp[direction === Direction.Inbound ? 'inboundAllowed' : 'outboundAllowed'] = isAllow;
        setApps(appsCopy);

        promiseFeedbackFactory({
            promise: fwVersionStore.changeAppStatus(id, direction, isAllow),
            successCallback: (res: FwVersionContent | undefined) => {
                if (!res) return;
                if (res.managed_apps.length !== apps?.length)
                    toastSuccessUpdateManageApp();
            },
            errorCallback: () => {
                setApps(originalData);
            },
            notifObj: { show: false }
        });
    };

    const setDataFromFilter = (data: AppWithStatus[]) => {
        if(fwVersionStore.selectedFwVersion) {
            setApps(data);
        }
    };

    const getProperDisplay = () => {
        if(!apps) return null;
        const title = isAiDefenseFilter ? `Common AI Apps (${apps.length})` : `Managed Apps (${apps.length})`;
        if (apps?.length) {
            return <>
                <div className={'managed-apps-title'}>{title}</div>
                <div className="apps-item-content" data-testid={'app-group'}>
                    {apps.map(app => <AppItem key={app.id} managedApps={apps} updateApp={updateApp} app={app}
                                              setDependenciesWarningModalData={setDependenciesWarningModalData}
                                              hideRemove={isAiDefenseFilter}/>)}
                </div>
            </>;
        }
        return <NoResult
            title={`No ${filterLabel === AppsFilterNames.All ? 'Managed' : filterLabel} Apps`}
            subTitle={isAiDefenseFilter ? 'No AI Defense apps available.' : 'Tap +Add button to search for apps to manage.'}
                         iconType={IconType.APPS_EMPTY_STATE}/>;
    };

    const addSelectedApps = async (selectedList: ListItem[]) => {
        const selectedAppsIdsList: string[] = selectedList.map(selectedApp => selectedApp.key);
        promiseFeedbackFactory({
            promise: fwVersionStore.addManagedApps(selectedAppsIdsList),
            successCallback: () => {
                setAppsVisible(false);
                toastSuccessUpdateManageApp();
            },
            notifObj: { show: false }
        });
    };

    return <div className={`apps-page page-content ${kebabCase(filterLabel)}`} data-testid="apps">
        <div className="header-filter-wrapper">
            <Filters filterConfig={appsFiltersConfig}
                setData={setDataFromFilter}
                originalData={originalApps}
            />
            <TooltipDisableReason disableReason={fwVersionStore.getDisableReason}>
                <CustomButton prefixIcon={<CustomIcon type={IconType.PLUS}/>}
                              className={'secondary-btn add-service-btn'}
                              label={'Add'} isDisabled={!!fwVersionStore.getDisableReason || isAiDefenseFilter}
                              onClick={() => setAppsVisible(true)}/>
            </TooltipDisableReason>
        </div>
        {isAiDefenseFilter && <AiAppsUsageBanner />}
        {!(isAiDefenseFilter && apps?.length === 0) && <HeaderColumnMax/>}
        {!isAiDefenseFilter && <DefaultBehavior/>}
        <ManagedAppsSelectModal isOpen={appsVisible} setIsOpen={setAppsVisible}
                                selectedAppIds={managedAppIds}
                                saveApps={addSelectedApps}
                                alertMessage={'Selected apps may have dependent apps which will be allowed in your managed list.'}
                                shouldDisplayAlert={(!ruleStore.isAppsInboundAllowAll || !ruleStore.isAppsOutboundAllowAll)}
                                emptyStateMessage={'Search apps to add to your managed apps list.\nAdding apps will change their default behavior.'}
        />

        {(!apps || fwVersionStore.isLoading || !fwVersionStore.selectedFwVersion) && <Loader type="full-screen"/>}
        {getProperDisplay() }

        {promiseContextHolder}
        {dependenciesWarningModalData &&
            <DependenciesWarningModal open={dependenciesWarningModalData.open}
                                      setOpen={(open: boolean) => setDependenciesWarningModalData({
                                          ...dependenciesWarningModalData,
                                          open
                                      })}
                                      appName={dependenciesWarningModalData.appName}
                                      dependentApps={dependenciesWarningModalData.dependentApps}
                                      okCallback={dependenciesWarningModalData?.okCallback}
                                      action={dependenciesWarningModalData.action}/>
        }
    </div>;
});